import { useEffect, useMemo, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Edit3, Stethoscope, BookOpenCheck, Car, Repeat, GripVertical } from "lucide-react";
import api, { formatApiError, cachedGet, apiCache } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

// ── Date helpers ──────────────────────────────────────────────────────────
function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay() || 7; // Mon=1..Sun=7
  x.setDate(x.getDate() - (day - 1));
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmt(d) {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonthGrid(d) {
  // 6×7 grid starting Monday of the week containing the 1st
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeek(first);
}
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const blank = (date, workers) => ({
  kind: "shift",
  worker_id: workers[0]?.id || "",
  medical_staff_id: "",
  date: fmt(date),
  start_time: "09:00",
  end_time: "12:00",
  notes: "",
  appointment_notes: "",
  transport_required: false,
  // recurrence (only used at creation time)
  repeat: false,
  repeat_weekdays: [],
  repeat_until: "",
});

export default function RosterPage() {
  const { user } = useAuth();
  const [view, setView] = useState("week"); // 'week' | 'month'
  const [anchor, setAnchor] = useState(startOfWeek(new Date())); // week-start in week view, any day in month view
  const [rows, setRows] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Range visible on screen — drives the GET query.
  const range = useMemo(() => {
    if (view === "week") {
      return { start: anchor, end: addDays(anchor, 6) };
    }
    const gridStart = startOfMonthGrid(anchor);
    return { start: gridStart, end: addDays(gridStart, 41) };
  }, [view, anchor]);

  const load = useCallback(async () => {
    try {
      const rosterParams = { start: fmt(range.start), end: fmt(range.end) };
      await Promise.all([
        cachedGet("/roster", rosterParams, (data) => setRows(data)),
        cachedGet("/workers", null, (data) => setWorkers(data)),
        cachedGet("/medical-staff", null, (data) => setStaff(data)),
      ]);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const open_ = (date, item) => {
    setEditing(item ? { ...item, repeat: false, repeat_weekdays: [], repeat_until: "" } : blank(date, workers));
    setOpen(true);
  };

  const save = async () => {
    if (editing.kind === "shift" && !editing.worker_id) { toast.error("Pick a worker"); return; }
    if (editing.kind === "appointment" && !editing.medical_staff_id) { toast.error("Pick the medical staff"); return; }
    const { repeat, repeat_weekdays, repeat_until, ...payload } = editing;
    if (payload.kind === "shift") payload.medical_staff_id = null;
    if (payload.kind === "appointment") payload.worker_id = payload.worker_id || null;
    try {
      // Existing row: never recurring — just update or create.
      if (editing.id) {
        const r = await api.put(`/roster/${editing.id}`, payload);
        if (r.data?.pending_approval) toast("Sent to client for approval");
        else toast.success("Updated");
      } else if (repeat) {
        if (user?.role !== "client") {
          toast.error("Only the client can create recurring shifts.");
          return;
        }
        if (!repeat_weekdays?.length) { toast.error("Pick at least one weekday."); return; }
        if (!repeat_until) { toast.error("Pick an end date for the recurrence."); return; }
        const r = await api.post("/roster/recurring", {
          kind: payload.kind,
          worker_id: payload.worker_id || null,
          medical_staff_id: payload.medical_staff_id || null,
          weekdays: repeat_weekdays,
          start_date: payload.date,
          end_date: repeat_until,
          start_time: payload.start_time,
          end_time: payload.end_time,
          notes: payload.notes || "",
          appointment_notes: payload.appointment_notes || "",
          transport_required: !!payload.transport_required,
        });
        toast.success(`${r.data.count} shift${r.data.count === 1 ? "" : "s"} added`);
      } else {
        const r = await api.post("/roster", payload);
        if (r.data?.status === "pending") toast("Sent to client for approval");
        else toast.success("Added");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      const r = await api.delete(`/roster/${id}`);
      if (r.data?.pending_approval) toast("Deletion sent to client for approval");
      else toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ── Drag-and-drop: move a shift to a different date ────────────────────
  const onDragStart = (id, e) => {
    setDraggingId(id);
    // Stash on dataTransfer so the drop handler can recover the id even if
    // React hasn't flushed setDraggingId yet (synthetic events, fast drags).
    try { e?.dataTransfer?.setData("text/x-roster-id", id); e.dataTransfer.effectAllowed = "move"; } catch { /* noop */ }
  };
  const onDragEnd = () => { setDraggingId(null); setDragOver(null); };
  const onDropDay = async (targetDateStr, e) => {
    let id = draggingId;
    try {
      const fromDt = e?.dataTransfer?.getData("text/x-roster-id");
      if (fromDt) id = fromDt;
    } catch { /* noop */ }
    setDraggingId(null); setDragOver(null);
    if (!id) return;
    const item = rows.find((r) => r.id === id);
    if (!item || item.date === targetDateStr) return;
    // Optimistic update
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, date: targetDateStr } : r)));
    try {
      const payload = {
        kind: item.kind,
        worker_id: item.worker_id || null,
        medical_staff_id: item.medical_staff_id || null,
        date: targetDateStr,
        start_time: item.start_time,
        end_time: item.end_time,
        notes: item.notes || "",
        appointment_notes: item.appointment_notes || "",
        transport_required: !!item.transport_required,
      };
      const r = await api.put(`/roster/${id}`, payload);
      if (r.data?.pending_approval) toast(`Move to ${targetDateStr} sent for approval`);
      else toast.success(`Moved to ${targetDateStr}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  // ── Quick inline time edit (saves on blur) ─────────────────────────────
  const updateTime = async (item, field, value) => {
    if (item[field] === value) return;
    setRows((prev) => prev.map((r) => (r.id === item.id ? { ...r, [field]: value } : r)));
    try {
      const payload = {
        kind: item.kind,
        worker_id: item.worker_id || null,
        medical_staff_id: item.medical_staff_id || null,
        date: item.date,
        start_time: field === "start_time" ? value : item.start_time,
        end_time: field === "end_time" ? value : item.end_time,
        notes: item.notes || "",
        appointment_notes: item.appointment_notes || "",
        transport_required: !!item.transport_required,
      };
      const r = await api.put(`/roster/${item.id}`, payload);
      if (r.data?.pending_approval) toast("Time change sent for approval");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────
  const goPrev = () => {
    if (view === "week") setAnchor(addDays(anchor, -7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  };
  const goNext = () => {
    if (view === "week") setAnchor(addDays(anchor, 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  };
  const goToday = () => {
    const today = new Date();
    setAnchor(view === "week" ? startOfWeek(today) : new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const headerLabel = view === "week"
    ? `Week of ${fmt(range.start)} → ${fmt(range.end)}`
    : anchor.toLocaleDateString("en-AU", { month: "long", year: "numeric" });

  return (
    <div className="space-y-5 max-w-7xl" data-testid="roster-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight">Roster</h1>
          <p className="text-muted-foreground">
            {headerLabel}
            {user?.role === "worker" && <span className="ml-2 text-xs italic">· Your changes go to the client for approval.</span>}
          </p>
          <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
            Tip: drag a shift to another day to reschedule it. Click the time to edit it inline.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {/* View toggle */}
          <div className="inline-flex p-1 rounded-full bg-muted" data-testid="view-toggle">
            <button
              onClick={() => setView("week")}
              data-testid="view-week"
              className={`px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition ${view === "week" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
            >Week</button>
            <button
              onClick={() => setView("month")}
              data-testid="view-month"
              className={`px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition ${view === "month" ? "bg-background shadow-sm" : "text-muted-foreground"}`}
            >Month</button>
          </div>
          {/* Navigation */}
          <Button variant="outline" className="rounded-full font-bold" onClick={goPrev} data-testid="prev-period">←</Button>
          <Button variant="outline" className="rounded-full font-bold" onClick={goToday} data-testid="this-period">Today</Button>
          <Button variant="outline" className="rounded-full font-bold" onClick={goNext} data-testid="next-period">→</Button>
          {user?.role === "client" && (
            <Button onClick={() => open_(new Date())} data-testid="add-shift-btn"
                    className="rounded-full font-bold gap-1" style={{ background: "hsl(var(--client))" }}>
              <Plus className="w-4 h-4" /> Add
            </Button>
          )}
        </div>
      </header>

      {view === "week"
        ? <WeekGrid days={Array.from({ length: 7 }, (_, i) => addDays(anchor, i))}
                    rows={rows} onAdd={open_} onEdit={open_} onDelete={del}
                    onDragStart={onDragStart} onDragEnd={onDragEnd} onDropDay={onDropDay}
                    dragOver={dragOver} setDragOver={setDragOver} draggingId={draggingId}
                    updateTime={updateTime} />
        : <MonthGrid anchor={anchor} rows={rows} onAdd={open_} onEdit={open_} onDelete={del}
                     onDragStart={onDragStart} onDragEnd={onDragEnd} onDropDay={onDropDay}
                     dragOver={dragOver} setDragOver={setDragOver} draggingId={draggingId} />}

      <ShiftDialog open={open} setOpen={setOpen} editing={editing} setEditing={setEditing}
                   workers={workers} staff={staff} save={save} userRole={user?.role} />
    </div>
  );
}

// ── Week view ─────────────────────────────────────────────────────────────
function WeekGrid({ days, rows, onAdd, onEdit, onDelete, onDragStart, onDragEnd, onDropDay, dragOver, setDragOver, draggingId, updateTime }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="week-grid">
      {days.map((d) => {
        const ds = fmt(d);
        const items = rows.filter((r) => r.date === ds);
        const isToday = ds === fmt(new Date());
        return (
          <Card
            key={ds}
            data-testid={`day-${ds}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(ds); }}
            onDragLeave={() => setDragOver((s) => (s === ds ? null : s))}
            onDrop={(e) => onDropDay(ds, e)}
            className={`rounded-3xl border-2 p-3 min-h-[200px] transition ${dragOver === ds ? "ring-2 ring-client bg-client/5" : ""} ${isToday ? "border-client" : ""}`}
          >
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{d.toLocaleDateString("en-AU", { weekday: "short" })}</div>
                <div className={`font-display font-black text-2xl ${isToday ? "text-client" : ""}`}>{d.getDate()}</div>
              </div>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => onAdd(d)} data-testid={`add-shift-${ds}`}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-1.5">
              {items.map((it) => (
                <RosterChip
                  key={it.id} item={it} pending={it.status === "pending"}
                  onEdit={() => onEdit(d, it)} onDelete={() => onDelete(it.id)}
                  onDragStart={(e) => onDragStart(it.id, e)} onDragEnd={onDragEnd}
                  isGhost={draggingId === it.id}
                  updateTime={updateTime}
                />
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// ── Month view ────────────────────────────────────────────────────────────
function MonthGrid({ anchor, rows, onAdd, onEdit, onDelete, onDragStart, onDragEnd, onDropDay, dragOver, setDragOver, draggingId }) {
  const start = startOfMonthGrid(anchor);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const monthIdx = anchor.getMonth();
  const todayStr = fmt(new Date());
  return (
    <Card className="rounded-3xl border-2 p-3 sm:p-4" data-testid="month-grid">
      <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center mb-2">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-[10px] sm:text-xs uppercase tracking-wider font-bold text-muted-foreground py-1">{w}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-2">
        {cells.map((d) => {
          const ds = fmt(d);
          const items = rows.filter((r) => r.date === ds);
          const inMonth = d.getMonth() === monthIdx;
          const isToday = ds === todayStr;
          return (
            <div
              key={ds}
              data-testid={`day-${ds}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(ds); }}
              onDragLeave={() => setDragOver((s) => (s === ds ? null : s))}
              onDrop={(e) => onDropDay(ds, e)}
              className={`relative rounded-xl border p-1.5 min-h-[88px] sm:min-h-[110px] flex flex-col gap-1 transition group
                ${inMonth ? "bg-background" : "bg-muted/30 text-muted-foreground/60"}
                ${dragOver === ds ? "ring-2 ring-client bg-client/5" : ""}
                ${isToday ? "border-client border-2" : ""}`}
            >
              <div className="flex items-center justify-between">
                <div className={`text-xs sm:text-sm font-bold ${isToday ? "text-client" : ""}`}>{d.getDate()}</div>
                {inMonth && (
                  <button
                    onClick={() => onAdd(d)}
                    data-testid={`add-shift-${ds}`}
                    className="text-muted-foreground/0 group-hover:text-muted-foreground hover:text-foreground transition"
                    aria-label="Add shift"
                  ><Plus className="w-3.5 h-3.5" /></button>
                )}
              </div>
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {items.slice(0, 3).map((it) => (
                  <MonthChip
                    key={it.id} item={it} pending={it.status === "pending"}
                    onClick={() => onEdit(d, it)} onDelete={() => onDelete(it.id)}
                    onDragStart={(e) => onDragStart(it.id, e)} onDragEnd={onDragEnd}
                    isGhost={draggingId === it.id}
                  />
                ))}
                {items.length > 3 && (
                  <button onClick={() => onEdit(d, items[3])} className="text-[10px] font-bold text-muted-foreground hover:text-foreground self-start"
                          data-testid={`more-${ds}`}>+{items.length - 3} more</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Reusable chips ────────────────────────────────────────────────────────
function RosterChip({ item, pending, onEdit, onDelete, onDragStart, onDragEnd, isGhost, updateTime }) {
  const isAppt = item.kind === "appointment";
  const cls = isAppt ? "bg-coral/10 border-coral/30" : "bg-client/10 border-client/20";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`rounded-xl px-2.5 py-2 text-xs font-semibold border ${cls} ${pending ? "ring-1 ring-amber-400" : ""} ${isGhost ? "opacity-30" : ""} cursor-grab active:cursor-grabbing`}
      data-testid={`shift-${item.id}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-bold flex items-center gap-1 min-w-0">
          <GripVertical className="w-3 h-3 text-muted-foreground shrink-0" />
          {isAppt ? <Stethoscope className="w-3 h-3 text-coral shrink-0" /> : <BookOpenCheck className="w-3 h-3 text-client shrink-0" />}
          <input
            type="time"
            value={item.start_time}
            onChange={(e) => updateTime?.(item, "start_time", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`time-start-${item.id}`}
            className="bg-transparent border-0 px-0 py-0 h-auto text-xs font-bold w-[58px] focus:outline-none focus:ring-0"
          />
          –
          <input
            type="time"
            value={item.end_time}
            onChange={(e) => updateTime?.(item, "end_time", e.target.value)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`time-end-${item.id}`}
            className="bg-transparent border-0 px-0 py-0 h-auto text-xs font-bold w-[58px] focus:outline-none focus:ring-0"
          />
        </span>
        <div className="flex gap-0.5">
          <button onClick={onEdit} className="text-muted-foreground hover:text-foreground" data-testid={`edit-shift-${item.id}`}><Edit3 className="w-3 h-3" /></button>
          <button onClick={onDelete} className="text-destructive" data-testid={`delete-shift-${item.id}`}><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
      {isAppt ? (
        <>
          <div className="text-foreground/80 truncate">{item.medical_staff_name || "—"}</div>
          {item.medical_staff_specialty && <div className="text-muted-foreground truncate">{item.medical_staff_specialty}</div>}
          {item.transport_required && <div className="text-coral text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 mt-0.5"><Car className="w-2.5 h-2.5" /> Transport</div>}
        </>
      ) : (
        <>
          <div className="text-foreground/80 truncate">{item.worker_name}</div>
          {item.notes && <div className="text-muted-foreground truncate">{item.notes}</div>}
        </>
      )}
      {pending && <div className="text-amber-600 dark:text-amber-300 text-[10px] uppercase tracking-wider font-bold mt-0.5">Pending approval</div>}
    </div>
  );
}

function MonthChip({ item, pending, onClick, onDelete, onDragStart, onDragEnd, isGhost }) {
  const isAppt = item.kind === "appointment";
  const cls = isAppt ? "bg-coral/15 border-coral/40 text-coral-foreground" : "bg-client/15 border-client/40 text-client-foreground";
  const label = isAppt ? (item.medical_staff_name || "Appt") : (item.worker_name || "Shift");
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onContextMenu={(e) => { e.preventDefault(); onDelete?.(); }}
      title={`${item.start_time}–${item.end_time} · ${label}${pending ? " · pending" : ""}`}
      className={`rounded-md border px-1 py-0.5 text-[10px] sm:text-[11px] font-semibold leading-tight truncate cursor-pointer ${cls} ${pending ? "ring-1 ring-amber-400" : ""} ${isGhost ? "opacity-30" : ""}`}
      data-testid={`shift-${item.id}`}
    >
      <span className="font-bold">{item.start_time.slice(0, 5)}</span> {label}
    </div>
  );
}

// ── Dialog ────────────────────────────────────────────────────────────────
function ShiftDialog({ open, setOpen, editing, setEditing, workers, staff, save, userRole }) {
  const isClient = userRole === "client";
  const isNew = !editing?.id;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="shift-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {editing?.id ? "Edit entry" : "Add to roster"}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">Pick a worker shift or a medical appointment, then fill the timing.</DialogDescription>
        </DialogHeader>
        {editing && (
          <div className="space-y-3">
            {/* Kind toggle */}
            <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-2xl">
              <KindBtn active={editing.kind === "shift"} icon={BookOpenCheck} label="Worker shift"
                       onClick={() => setEditing((e) => ({ ...e, kind: "shift" }))} testId="kind-shift" />
              <KindBtn active={editing.kind === "appointment"} icon={Stethoscope} label="Medical appointment"
                       onClick={() => setEditing((e) => ({ ...e, kind: "appointment" }))} testId="kind-appointment" />
            </div>

            {/* Common fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Date</Label><Input type="date" value={editing.date} onChange={(e) => setEditing((s) => ({ ...s, date: e.target.value }))} className="rounded-xl min-h-[48px]" data-testid="shift-date" /></div>
              <div /> {/* spacer */}
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Start</Label><Input type="time" value={editing.start_time} onChange={(e) => setEditing((s) => ({ ...s, start_time: e.target.value }))} className="rounded-xl min-h-[48px]" data-testid="shift-start" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">End</Label><Input type="time" value={editing.end_time} onChange={(e) => setEditing((s) => ({ ...s, end_time: e.target.value }))} className="rounded-xl min-h-[48px]" data-testid="shift-end" /></div>
            </div>

            {editing.kind === "shift" ? (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-bold tracking-wider">Worker</Label>
                  <Select value={editing.worker_id || ""} onValueChange={(v) => setEditing((e) => ({ ...e, worker_id: v }))}>
                    <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="shift-worker"><SelectValue placeholder="Choose worker" /></SelectTrigger>
                    <SelectContent>
                      {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Notes</Label><Input value={editing.notes || ""} onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))} className="rounded-xl min-h-[48px]" data-testid="shift-notes" placeholder="Personal care · weekday day · etc." /></div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-bold tracking-wider">Appointment with</Label>
                  <Select value={editing.medical_staff_id || ""} onValueChange={(v) => setEditing((e) => ({ ...e, medical_staff_id: v }))}>
                    <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="appointment-staff">
                      <SelectValue placeholder={staff.length === 0 ? "Add staff first under Treating Medical Staff" : "Choose staff"} />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.specialty ? ` · ${s.specialty}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {staff.length === 0 && (
                    <p className="text-xs text-muted-foreground">Tip: add doctors and allied health on the <a href="/medical-staff" className="underline font-bold">Treating Medical Staff</a> page first.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase font-bold tracking-wider">Worker accompanying (optional)</Label>
                  <Select value={editing.worker_id || "_none"} onValueChange={(v) => setEditing((e) => ({ ...e, worker_id: v === "_none" ? "" : v }))}>
                    <SelectTrigger className="rounded-xl min-h-[48px]" data-testid="appointment-worker"><SelectValue placeholder="No-one" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">No worker</SelectItem>
                      {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input type="checkbox" checked={!!editing.transport_required}
                         onChange={(e) => setEditing((s) => ({ ...s, transport_required: e.target.checked }))}
                         data-testid="appointment-transport" />
                  <Car className="w-3.5 h-3.5" /> Transport required
                </label>
                <div className="space-y-1.5"><Label className="text-xs uppercase font-bold tracking-wider">Appointment notes</Label><Input value={editing.appointment_notes || ""} onChange={(e) => setEditing((s) => ({ ...s, appointment_notes: e.target.value }))} className="rounded-xl min-h-[48px]" data-testid="appointment-notes" placeholder="Bring referral, fasting required…" /></div>
              </>
            )}

            {/* Recurrence — only for new entries, client only */}
            {isNew && isClient && (
              <RecurrenceSection editing={editing} setEditing={setEditing} />
            )}
          </div>
        )}
        <DialogFooter><Button onClick={save} className="rounded-full font-bold"
                              style={{ background: editing?.kind === "appointment" ? "hsl(var(--coral))" : "hsl(var(--client))",
                                        color: editing?.kind === "appointment" ? "white" : undefined }}
                              data-testid="shift-save-btn">
          {editing?.repeat ? "Add recurring shifts" : "Save"}
        </Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecurrenceSection({ editing, setEditing }) {
  const toggleDay = (idx) => {
    setEditing((s) => {
      const set = new Set(s.repeat_weekdays || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      return { ...s, repeat_weekdays: Array.from(set).sort() };
    });
  };
  // Default end date = 4 weeks after the start date
  const seedUntil = () => {
    if (editing.repeat_until) return editing.repeat_until;
    if (!editing.date) return "";
    const d = new Date(editing.date);
    d.setDate(d.getDate() + 28);
    return fmt(d);
  };
  return (
    <div className="rounded-2xl border-2 border-dashed p-3 space-y-2 bg-muted/30">
      <label className="flex items-center gap-2 cursor-pointer text-sm font-bold">
        <input type="checkbox" checked={!!editing.repeat}
               data-testid="repeat-toggle"
               onChange={(e) => setEditing((s) => ({
                 ...s,
                 repeat: e.target.checked,
                 // pre-select the chosen date's weekday + 4-week window when first turning on
                 repeat_weekdays: e.target.checked && (s.repeat_weekdays?.length ?? 0) === 0
                   ? [(new Date(s.date).getDay() + 6) % 7] : s.repeat_weekdays,
                 repeat_until: e.target.checked && !s.repeat_until ? seedUntil() : s.repeat_until,
               }))} />
        <Repeat className="w-4 h-4" /> Repeat this shift
      </label>
      {editing.repeat && (
        <div className="space-y-2 pt-1">
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">On these days</Label>
            <div className="flex gap-1 mt-1.5 flex-wrap">
              {WEEKDAY_LABELS.map((lbl, i) => {
                const active = editing.repeat_weekdays?.includes(i);
                return (
                  <button
                    type="button"
                    key={lbl}
                    onClick={() => toggleDay(i)}
                    data-testid={`repeat-day-${i}`}
                    className={`rounded-full px-2.5 py-1 text-xs font-bold border transition ${active ? "bg-client text-white border-client" : "bg-background text-muted-foreground border-muted-foreground/20 hover:text-foreground"}`}
                  >{lbl}</button>
                );
              })}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold tracking-wider">Until</Label>
            <Input type="date" value={editing.repeat_until || ""}
                   onChange={(e) => setEditing((s) => ({ ...s, repeat_until: e.target.value }))}
                   data-testid="repeat-until" className="rounded-xl min-h-[44px] mt-1.5" />
          </div>
          <p className="text-xs text-muted-foreground">
            One shift will be added for each selected weekday between <b>{editing.date}</b> and the end date.
          </p>
        </div>
      )}
    </div>
  );
}

function KindBtn({ active, icon: Icon, label, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
            className={`rounded-xl px-3 py-2.5 text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition ${
              active ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"
            }`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
